/**
 * ToastNotification Component
 *
 * Enterprise-grade toast notification system with modern UI/UX.
 * Features:
 * - Multiple severity levels (success, error, warning, info)
 * - Auto-dismiss with configurable duration
 * - Manual close button
 * - Smooth animations with Material-UI transitions
 * - Gradient accents matching application theme
 * - Icon support with contextual colors
 * - Responsive positioning (top-center by default)
 * - Accessible with ARIA labels
 * - Professional box shadows and hover effects
 */

import React from "react";
import { Snackbar, Alert, AlertTitle, IconButton } from "@mui/material";
import {
  CheckCircle,
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
  Close,
} from "@mui/icons-material";

export interface ToastNotificationProps {
  /** Whether the toast is visible */
  open: boolean;
  /** Handler called when toast should close */
  onClose: () => void;
  /** Toast message content */
  message: string;
  /** Optional title for the toast */
  title?: string;
  /** Severity level determines color and icon */
  severity?: "success" | "error" | "warning" | "info";
  /** Auto-hide duration in milliseconds (0 = no auto-hide) */
  autoHideDuration?: number;
  /** Vertical position */
  vertical?: "top" | "bottom";
  /** Horizontal position */
  horizontal?: "left" | "center" | "right";
  /** Optional action button text and handler */
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * ToastNotification Component
 *
 * @example
 * // Success toast
 * <ToastNotification
 *   open={true}
 *   onClose={() => {}}
 *   message="Data saved successfully!"
 *   severity="success"
 * />
 *
 * @example
 * // Error toast with title and custom duration
 * <ToastNotification
 *   open={true}
 *   onClose={() => {}}
 *   title="Operation Failed"
 *   message="Failed to load data. Please try again."
 *   severity="error"
 *   autoHideDuration={8000}
 * />
 *
 * @example
 * // Warning toast with action button
 * <ToastNotification
 *   open={true}
 *   onClose={() => {}}
 *   message="Your session will expire soon"
 *   severity="warning"
 *   action={{ label: "Extend", onClick: handleExtend }}
 * />
 */
const ToastNotification: React.FC<ToastNotificationProps> = ({
  open,
  onClose,
  message,
  title,
  severity = "info",
  autoHideDuration = 6000,
  vertical = "top",
  horizontal = "center",
  action,
}) => {
  // Gradient backgrounds for each severity level
  const getSeverityGradient = () => {
    switch (severity) {
      case "success":
        return "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)";
      case "error":
        return "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)";
      case "warning":
        return "linear-gradient(135deg, #fa709a 0%, #fee140 100%)";
      case "info":
        return "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)";
      default:
        return "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)";
    }
  };

  // Icon for each severity level
  const getSeverityIcon = () => {
    switch (severity) {
      case "success":
        return <CheckCircle />;
      case "error":
        return <ErrorOutline />;
      case "warning":
        return <WarningAmber />;
      case "info":
        return <InfoOutlined />;
      default:
        return <InfoOutlined />;
    }
  };

  // Handle Snackbar close with proper event handling
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    // Don't close on clickaway
    if (reason === "clickaway") {
      return;
    }
    onClose();
  };

  // Handle manual close button click
  const handleManualClose = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClose();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration > 0 ? autoHideDuration : null}
      onClose={handleClose}
      anchorOrigin={{ vertical, horizontal }}
      disableWindowBlurListener={true}
      ClickAwayListenerProps={{
        mouseEvent: false,
        touchEvent: false,
      }}
      sx={{
        // Toast appears above sidebar and content
        // snackbar default is 1400, drawer is 1200
        zIndex: (theme) => theme.zIndex.snackbar,
        // Add margin for mobile devices
        mt: vertical === "top" ? { xs: 2, sm: 3 } : 0,
        mb: vertical === "bottom" ? { xs: 2, sm: 3 } : 0,
        // Prevent pointer events on the snackbar container itself
        // but allow them on the alert content
        pointerEvents: "none",
        "& .MuiAlert-root": {
          pointerEvents: "auto",
        },
      }}
    >
      <Alert
        severity={severity}
        variant="filled"
        icon={getSeverityIcon()}
        sx={{
          minWidth: { xs: "260px", sm: "320px" },
          maxWidth: { xs: "85vw", sm: "420px" },
          // Professional box shadow
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          // Use gradient backgrounds for modern look
          background: getSeverityGradient(),
          // Remove default background color
          bgcolor: "transparent",
          // Icon styling
          "& .MuiAlert-icon": {
            color: "white",
            fontSize: 20,
            alignItems: "center",
          },
          // Message styling
          "& .MuiAlert-message": {
            color: "white",
            fontSize: "0.875rem",
            fontWeight: 500,
            width: "100%",
          },
          // Close button styling
          "& .MuiAlert-action": {
            paddingTop: title ? "2px" : "0",
          },
          // Border radius for modern look
          borderRadius: "8px",
          // Compact padding
          py: 1,
          px: 1.5,
          // Position relative for closing bar
          position: "relative",
          overflow: "visible",
        }}
        action={
          <>
            {/* Optional action button */}
            {action && (
              <IconButton
                size="small"
                onClick={() => {
                  action.onClick();
                  onClose();
                }}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.2)",
                  mr: 0.5,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.3)",
                  },
                  borderRadius: "6px",
                  textTransform: "none",
                }}
              >
                {action.label}
              </IconButton>
            )}
            {/* Close button */}
            <IconButton
              size="small"
              onClick={handleManualClose}
              sx={{
                color: "white",
                p: 0.5,
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.2)",
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </>
        }
      >
        {/* Optional title */}
        {title && (
          <AlertTitle
            sx={{
              color: "white",
              fontWeight: 600,
              fontSize: "0.875rem",
              mb: 0.25,
            }}
          >
            {title}
          </AlertTitle>
        )}
        {/* Message content */}
        {message}

        {/* Closing bar animation */}
        {autoHideDuration > 0 && open && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              height: "3px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "0 0 8px 8px",
              animation: `shrink ${autoHideDuration}ms linear forwards`,
              width: "100%",
              boxShadow: "0 0 8px rgba(255, 255, 255, 0.5)",
            }}
          />
        )}

        {/* Keyframe animation for closing bar */}
        <style>
          {`
            @keyframes shrink {
              from {
                width: 100%;
              }
              to {
                width: 0%;
              }
            }
          `}
        </style>
      </Alert>
    </Snackbar>
  );
};

export default ToastNotification;
